import { useMemo } from "react";

import { combineExtents } from "@nens/shared-utils";

import { usePmTilesFeaturesQuery } from "@/queries/query-pmtiles-features";
import { usePmTilesHeaderQuery } from "@/queries/query-pmtiles-header";
import { useStaticFileQuery } from "@/queries/query-static-file";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { FeatureSelectorFieldProps } from "../types/feature-selector-field-props-type";

export function useSelectedFileStandard({ formData }: FeatureSelectorFieldProps, path: string) {
  const { data: pmTiles, isLoading: isLoadingPmTiles } = useStaticFileQuery(path);
  const pmTilesUrl = pmTiles?.url;
  const { data: pmTilesHeader, isLoading: isLoadingPmTilesHeader } =
    usePmTilesHeaderQuery(pmTilesUrl);

  const { data: pmTilesFeatures, isLoading: isLoadingPmTilesFeatures } =
    usePmTilesFeaturesQuery(pmTilesUrl);

  const preferProjectLocation = !formData; // Prefer the project location when no feature is selected yet because that would then almost always just be the extent of the netherlands

  const bounds: [number, number, number, number] | null = useMemo(() => {
    if (!pmTilesHeader) return null;
    if (!preferProjectLocation) {
      if (pmTilesFeatures) {
        const filteredFeatures = pmTilesFeatures.filter((f) =>
          formData.feature_ids?.includes(Number(f.id))
        );
        return combineExtents(filteredFeatures.map((f) => f.bounds));
      }
      return null;
    }
    const { minLon, minLat, maxLon, maxLat } = pmTilesHeader;
    return [minLon, minLat, maxLon, maxLat];
  }, [preferProjectLocation, formData, pmTilesFeatures, pmTilesHeader]);

  const layerName = path.replace(/^.*\/(.*?)\..*$/, "$1"); // gets layer name from boundary path

  const descriptorId = "none";

  const layer = useMemo(() => {
    if (!layerName || !pmTilesUrl) return null;

    return {
      descriptorId: "none",
      layer: layerName,
      type: "GeometryCollection",
      url: pmTilesUrl,
    } as const;
  }, [pmTilesUrl, layerName]);

  const fileTitle = getNameFromFileId(path ?? "");

  return {
    descriptorId,
    layer,
    layerName,
    isLoading: isLoadingPmTiles || isLoadingPmTilesHeader || isLoadingPmTilesFeatures,
    bounds,
    fileTitle,
    pmTilesUrl,
    preferProjectLocation,
  } as const;
}
